/**
 * FE-only persistence for the first-login onboarding flow.
 *
 * Records are keyed per wallet address in localStorage, so a wallet re-runs
 * onboarding on a fresh browser/device but never twice in the same one.
 *
 * NOTE: this module is intentionally the single seam for onboarding
 * persistence — when the backend acceptance endpoint lands, swap the
 * internals here and every caller stays unchanged.
 */

const STORAGE_PREFIX = 'snappy.onboarding.v1.';

export interface OnboardingRecord {
    /** ISO timestamp of terms/privacy/cookie acceptance, null if not yet. */
    termsAcceptedAt: string | null;
    /**
     * ISO timestamp of the restricted-jurisdiction attestation (not a US
     * person, not trading from the US), null if not yet. Records written
     * before this box existed read as null, and are deliberately NOT
     * re-prompted: `termsAcceptedAt` alone still gates the terms step.
     */
    jurisdictionAttestedAt: string | null;
    /** ISO timestamp when the flow finished, null while incomplete. */
    completedAt: string | null;
    /**
     * Onboarding was launched from a trading-window attempt that dead-ended on
     * a missing Snappy account, so finishing it has to drop the user back into
     * that ceremony. The launcher store holds the same flag, but only in
     * memory: closing the app would otherwise lose the intent, and the user
     * would finish onboarding into nothing.
     */
    resumeReauthOnFinish: boolean;
}

const EMPTY_RECORD: OnboardingRecord = {
    termsAcceptedAt: null,
    jurisdictionAttestedAt: null,
    completedAt: null,
    resumeReauthOnFinish: false,
};

function storageKey(address: string): string {
    return `${STORAGE_PREFIX}${address.toLowerCase()}`;
}

export function getOnboardingRecord(address: string): OnboardingRecord {
    try {
        const raw = window.localStorage.getItem(storageKey(address));
        if (!raw) return { ...EMPTY_RECORD };
        const parsed = JSON.parse(raw) as Partial<OnboardingRecord>;
        return {
            termsAcceptedAt:
                typeof parsed.termsAcceptedAt === 'string'
                    ? parsed.termsAcceptedAt
                    : null,
            jurisdictionAttestedAt:
                typeof parsed.jurisdictionAttestedAt === 'string'
                    ? parsed.jurisdictionAttestedAt
                    : null,
            completedAt:
                typeof parsed.completedAt === 'string'
                    ? parsed.completedAt
                    : null,
            resumeReauthOnFinish: parsed.resumeReauthOnFinish === true,
        };
    } catch {
        return { ...EMPTY_RECORD };
    }
}

function saveRecord(address: string, record: OnboardingRecord): void {
    try {
        window.localStorage.setItem(
            storageKey(address),
            JSON.stringify(record),
        );
    } catch {
        // storage unavailable — onboarding will simply re-show next session
    }
}

/**
 * Stamps the terms step's acceptance. Both boxes are ticked in the same
 * dialog, so the jurisdiction attestation is stamped alongside the terms.
 */
export function markTermsAccepted(address: string): void {
    const record = getOnboardingRecord(address);
    const now = new Date().toISOString();
    saveRecord(address, {
        ...record,
        termsAcceptedAt: record.termsAcceptedAt ?? now,
        jurisdictionAttestedAt: record.jurisdictionAttestedAt ?? now,
    });
}

/**
 * Records (or drops) the intent to reopen the interrupted trading-window
 * ceremony once onboarding finishes.
 */
export function setOnboardingResumeReauth(
    address: string,
    resume: boolean,
): void {
    const record = getOnboardingRecord(address);
    if (record.resumeReauthOnFinish === resume) return;
    saveRecord(address, { ...record, resumeReauthOnFinish: resume });
}

export function markOnboardingCompleted(address: string): void {
    const record = getOnboardingRecord(address);
    saveRecord(address, {
        ...record,
        completedAt: record.completedAt ?? new Date().toISOString(),
    });
}
